import { useEffect, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Alert,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Stack,
  Paper,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Verified as VerifiedIcon,
  VerifiedUser as VerifiedUserIcon,
  Warning as WarningIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
  Star as StarIcon,
  EventAvailable as LineupIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { usersApi } from '../api/users';
import { ratingsApi } from '../api/ratings';
import { verificationApi, SleeperPreview } from '../api/verification';
import { getErrorMessage } from '../api/client';
import { SleeperAccountWithHistory } from '../types';
import { tokens } from '../theme';

export default function DashboardPage() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [sleeperUsername, setSleeperUsername] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [verificationMessage, setVerificationMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [copiedCode, setCopiedCode] = useState(false);
  
  // New state for combined link+verify flow
  const [previewData, setPreviewData] = useState<SleeperPreview | null>(null);
  const [linkStep, setLinkStep] = useState<'username' | 'verify'>('username');

  // Auto-poll: while the verify step is open, we silently call the verify
  // endpoint every 10s for up to 3 minutes so the user doesn't have to
  // click "Verify & Link" themselves once they've saved the team name in
  // Sleeper. `autoCheckState` drives the small indicator under the league
  // card so the user knows what's happening.
  const [autoCheckState, setAutoCheckState] = useState<
    'idle' | 'checking' | 'timeout'
  >('idle');

  // While > Date.now(), the sleeperAccounts query refetches every 3s so we
  // can watch leagues populate during a background sync. Set after verify
  // succeeds or the user kicks off a deep-sync.
  const [pollUntil, setPollUntil] = useState<number>(0);
  const isSyncing = pollUntil > Date.now();

  // Track which account is currently being deep-synced for spinner UX.
  const [syncingAccountId, setSyncingAccountId] = useState<string | null>(null);

  // Fetch sleeper accounts. `refetchInterval` returns a number (ms) while
  // a recent sync is in flight, else `false` to disable polling.
  const { data: sleeperAccounts, isLoading: accountsLoading } = useQuery({
    queryKey: ['sleeperAccounts'],
    queryFn: usersApi.getSleeperAccounts,
    refetchInterval: () => (Date.now() < pollUntil ? 3000 : false),
    refetchIntervalInBackground: false,
  });

  // Profile (used here for ratingStats now that the standalone Profile
  // page has been folded into the dashboard).
  const { data: profile } = useQuery({
    queryKey: ['profile'],
    queryFn: usersApi.getProfile,
  });

  // Fetch my ratings
  const { data: myRatings } = useQuery({
    queryKey: ['myRatings'],
    queryFn: () => ratingsApi.getMyRatings({ limit: 5 }),
  });

  // Deep-link support: `/dashboard?verify=1` auto-opens the link/verify
  // dialog. The VerifyBanner sends users here; clear the param after
  // consuming so a refresh doesn't reopen the dialog.
  useEffect(() => {
    if (searchParams.get('verify') === '1') {
      setLinkDialogOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete('verify');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Preview sleeper account mutation (step 1)
  const previewMutation = useMutation({
    mutationFn: (username: string) => verificationApi.preview(username),
    onSuccess: (data) => {
      setPreviewData(data);
      setLinkStep('verify');
      setLinkError(null);
    },
    onError: (error) => {
      setLinkError(getErrorMessage(error));
    },
  });

  // Verify and link mutation (step 2). Checks only the specific league
  // chosen during preview.
  const verifyAndLinkMutation = useMutation({
    mutationFn: ({
      sleeperUserId,
      leagueId,
      verificationCode,
    }: {
      sleeperUserId: string;
      leagueId: string;
      verificationCode: string;
    }) => verificationApi.verifyAndLink(sleeperUserId, leagueId, verificationCode),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['sleeperAccounts'] });
        // Poll for ~90s so the user watches their current-season leagues
        // populate live as the background sync completes.
        setPollUntil(Date.now() + 90_000);
        setVerificationMessage({
          type: 'success',
          text: result.message,
        });
        handleCloseLinkDialog();
      } else {
        setLinkError(result.message);
      }
    },
    onError: (error) => {
      setLinkError(getErrorMessage(error));
    },
  });

  // Deep sync = fetch the user's full multi-season league history. Fires
  // on the server in the background; we poll sleeperAccounts for progress.
  const deepSyncMutation = useMutation({
    mutationFn: (accountId: string) => usersApi.deepSyncLeagues(accountId),
    onMutate: (accountId) => {
      setSyncingAccountId(accountId);
    },
    onSuccess: () => {
      // Deep sync can take longer than a current-season sync; poll for
      // 3 minutes so the dashboard reflects the incoming history.
      setPollUntil(Date.now() + 3 * 60_000);
      setVerificationMessage({
        type: 'success',
        text: 'Syncing your league history in the background. This may take a minute or two.',
      });
    },
    onError: (err) => {
      setVerificationMessage({
        type: 'error',
        text: getErrorMessage(err),
      });
    },
    onSettled: () => {
      setSyncingAccountId(null);
    },
  });

  const handleCloseLinkDialog = () => {
    setLinkDialogOpen(false);
    setSleeperUsername('');
    setLinkError(null);
    setPreviewData(null);
    setLinkStep('username');
    setAutoCheckState('idle');
  };

  // Auto-check the team name every 10s for up to 3 minutes once the user
  // is on the verify step. We call the same `verifyAndLink` endpoint a
  // manual click would, but suppress the "team name doesn't match yet"
  // failure (the expected case while the user is still editing in
  // Sleeper). On a real success we run the same wrap-up the mutation
  // would. On a fatal failure (account already linked, user not in the
  // league, etc.) we stop polling and surface the error so the user
  // isn't left silently stuck.
  useEffect(() => {
    if (!linkDialogOpen || linkStep !== 'verify' || !previewData) return;

    let cancelled = false;
    const startedAt = Date.now();
    const MAX_DURATION_MS = 3 * 60 * 1000;
    const INTERVAL_MS = 10_000;
    let timeoutId: number | null = null;

    setAutoCheckState('checking');

    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt > MAX_DURATION_MS) {
        setAutoCheckState('timeout');
        return;
      }
      try {
        const result = await verificationApi.verifyAndLink(
          previewData.sleeperUser.user_id,
          previewData.verifyLeague.league_id,
          previewData.verificationCode
        );
        if (cancelled) return;
        if (result.success) {
          queryClient.invalidateQueries({ queryKey: ['sleeperAccounts'] });
          setPollUntil(Date.now() + 90_000);
          setVerificationMessage({ type: 'success', text: result.message });
          setAutoCheckState('idle');
          handleCloseLinkDialog();
          return;
        }
        // "Doesn't match yet" is the expected in-progress case — keep
        // polling silently. Anything else (already linked, not in the
        // league, etc.) is fatal: stop and show it.
        if (!/doesn'?t match|don'?t appear/i.test(result.message)) {
          setLinkError(result.message);
          setAutoCheckState('idle');
          return;
        }
      } catch {
        // Network blip — keep silent and try again on the next tick.
      }
      if (!cancelled) {
        timeoutId = window.setTimeout(tick, INTERVAL_MS);
      }
    };

    timeoutId = window.setTimeout(tick, INTERVAL_MS);

    return () => {
      cancelled = true;
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkDialogOpen, linkStep, previewData]);

  const handlePreviewAccount = () => {
    if (sleeperUsername.trim()) {
      previewMutation.mutate(sleeperUsername.trim());
    }
  };

  const handleVerifyAndLink = () => {
    if (previewData) {
      verifyAndLinkMutation.mutate({
        sleeperUserId: previewData.sleeperUser.user_id,
        leagueId: previewData.verifyLeague.league_id,
        verificationCode: previewData.verificationCode,
      });
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 700 }}>
        Dashboard
      </Typography>

      <Grid container spacing={3}>
        {/* Onboarding checklist — collapses to nothing once both steps are done. */}
        <OnboardingChecklist
          isVerified={!!user?.isVerified}
          hasRated={!!myRatings && myRatings.length > 0}
          onLinkClick={() => setLinkDialogOpen(true)}
        />

        {/* Welcome Card — also serves as the user's profile summary now
            that the standalone Profile page has been removed. Identity
            (avatar / name / email / chips) sits on the left, and the
            two ratings counts sit on the right at md+. On mobile the
            stats wrap underneath. */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', md: 'center' },
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 3 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                  <Avatar
                    src={user?.avatarUrl || undefined}
                    sx={{ width: 64, height: 64, bgcolor: 'primary.main', flexShrink: 0 }}
                  >
                    {user?.displayName?.[0]?.toUpperCase()}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }} noWrap>
                      Welcome, {user?.displayName}!
                    </Typography>
                    {user?.email && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }} noWrap>
                        {user.email}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                      {user?.isVerified ? (
                        <Chip
                          icon={<VerifiedIcon />}
                          label="Verified"
                          size="small"
                          color="success"
                        />
                      ) : (
                        <Chip
                          icon={<WarningIcon />}
                          label="Unverified"
                          size="small"
                          color="warning"
                        />
                      )}
                      {user?.isAdmin && (
                        <Chip label="Admin" size="small" color="primary" />
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* Compact ratings summary (received / given). Stacks
                    horizontally on every breakpoint since two numbers
                    fit comfortably side-by-side even on phones. */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    width: { xs: '100%', md: 'auto' },
                    flexShrink: 0,
                  }}
                >
                  <Box
                    sx={{
                      flex: { xs: 1, md: 'none' },
                      textAlign: 'center',
                      px: 2,
                      py: 1,
                      bgcolor: 'background.default',
                      borderRadius: 2,
                      minWidth: { md: 110 },
                    }}
                  >
                    <Typography
                      variant="h5"
                      color="secondary.main"
                      sx={{ fontWeight: 700, lineHeight: 1.1 }}
                    >
                      {profile?.ratingStats?.total_received || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ratings Received
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      flex: { xs: 1, md: 'none' },
                      textAlign: 'center',
                      px: 2,
                      py: 1,
                      bgcolor: 'background.default',
                      borderRadius: 2,
                      minWidth: { md: 110 },
                    }}
                  >
                    <Typography
                      variant="h5"
                      color="primary.main"
                      sx={{ fontWeight: 700, lineHeight: 1.1 }}
                    >
                      {profile?.ratingStats?.total_given || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Ratings Given
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sleeper Accounts */}
        <Grid item xs={12} md={7}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Linked Sleeper Account
                </Typography>
                {/* Only show Link Account button if user has no linked accounts */}
                {(!sleeperAccounts || sleeperAccounts.length === 0) && (
                  <Button
                    startIcon={<AddIcon />}
                    size="small"
                    onClick={() => setLinkDialogOpen(true)}
                  >
                    Link Account
                  </Button>
                )}
              </Box>

              {accountsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : sleeperAccounts && sleeperAccounts.length > 0 ? (
                <List>
                  {sleeperAccounts.map((account: SleeperAccountWithHistory) => (
                    <Box key={account.id}>
                      <ListItem sx={{ alignItems: 'flex-start' }}>
                        <ListItemAvatar>
                          <Avatar
                            src={
                              account.current_avatar_id
                                ? `https://sleepercdn.com/avatars/thumbs/${account.current_avatar_id}`
                                : undefined
                            }
                          >
                            {account.current_username?.[0]?.toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {account.current_username}
                              </Typography>
                              {account.is_verified && (
                                <VerifiedIcon
                                  sx={{ fontSize: 18, color: 'success.main' }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            account.is_verified ? (
                              <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Chip
                                  label="Verified"
                                  size="small"
                                  color="success"
                                />
                                {isSyncing && (
                                  <Chip
                                    icon={<CircularProgress size={12} thickness={6} sx={{ color: 'inherit' }} />}
                                    label="Syncing..."
                                    size="small"
                                    variant="outlined"
                                    sx={{ '& .MuiChip-icon': { ml: 0.75 } }}
                                  />
                                )}
                                <Button
                                  size="small"
                                  variant="text"
                                  startIcon={
                                    syncingAccountId === account.id ? (
                                      <CircularProgress size={14} thickness={5} />
                                    ) : (
                                      <HistoryIcon />
                                    )
                                  }
                                  disabled={deepSyncMutation.isPending}
                                  onClick={() => deepSyncMutation.mutate(account.id)}
                                >
                                  Sync history
                                </Button>
                                <Button
                                  size="small"
                                  variant="text"
                                  component={RouterLink}
                                  to={`/sleeper/${account.sleeper_user_id}/lineups`}
                                  startIcon={<LineupIcon />}
                                >
                                  Check my Lineups
                                </Button>
                              </Box>
                            ) : (
                              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  label="Not verified"
                                  size="small"
                                  color="warning"
                                  sx={{ mt: 0.5 }}
                                />
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => {
                                    setSleeperUsername(account.current_username);
                                    setLinkDialogOpen(true);
                                  }}
                                >
                                  Re-link to verify
                                </Button>
                              </Box>
                            )
                          }
                        />
                      </ListItem>
                    </Box>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" sx={{ mb: 2 }}>
                    No Sleeper accounts linked yet
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setLinkDialogOpen(true)}
                  >
                    Link Your Sleeper Account
                  </Button>
                </Box>
              )}

            </CardContent>
          </Card>
        </Grid>

        {/* Recent Ratings Given */}
        <Grid item xs={12} md={5}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Your Recent Ratings
              </Typography>

              {myRatings && myRatings.length > 0 ? (
                <List>
                  {myRatings.map((rating) => {
                    const avg = (
                      (rating.payment_reliability +
                        rating.communication +
                        rating.fair_play +
                        rating.league_commitment +
                        rating.fair_trader) /
                      5
                    ).toFixed(1);
                    const ratedAccount = rating.ratedAccount;
                    // Each row links to the rated user's profile so it's
                    // a one-click trip from "I rated this person" to
                    // "go look at what I wrote".
                    return (
                      <ListItem
                        key={rating.id}
                        component={ratedAccount ? RouterLink : 'div'}
                        to={
                          ratedAccount
                            ? `/sleeper/${ratedAccount.sleeper_user_id}`
                            : undefined
                        }
                        sx={{
                          textDecoration: 'none',
                          color: 'inherit',
                          borderRadius: 1,
                          '&:hover': ratedAccount
                            ? { bgcolor: 'action.hover' }
                            : undefined,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            src={
                              ratedAccount?.current_avatar_id
                                ? `https://sleepercdn.com/avatars/thumbs/${ratedAccount.current_avatar_id}`
                                : undefined
                            }
                            sx={{ width: 36, height: 36 }}
                          >
                            {ratedAccount?.current_username?.[0]?.toUpperCase() ?? '?'}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            ratedAccount
                              ? ratedAccount.current_username
                              : 'Rating given'
                          }
                          secondary={new Date(rating.created_at).toLocaleDateString()}
                        />
                        <Chip
                          label={`${avg} avg`}
                          color="secondary"
                          size="small"
                        />
                      </ListItem>
                    );
                  })}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    You haven't rated anyone yet
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Link Account Dialog - Combined Link + Verify Flow */}
      <Dialog
        open={linkDialogOpen}
        onClose={handleCloseLinkDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {linkStep === 'username' ? 'Link Sleeper Account' : 'Verify Your Account'}
        </DialogTitle>
        <DialogContent>
          {linkError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {linkError}
            </Alert>
          )}

          {linkStep === 'username' ? (
            // Step 1: Enter username
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your Sleeper username. You'll need to verify ownership by
                creating a temporary league in Sleeper.
              </Typography>
              <TextField
                autoFocus
                label="Sleeper Username"
                fullWidth
                value={sleeperUsername}
                onChange={(e) => setSleeperUsername(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handlePreviewAccount()}
                disabled={previewMutation.isPending}
              />
            </>
          ) : (
            // Step 2: Verification instructions — direct & visual.
            // Layout: small Sleeper user header → big "change team name to
            // [CODE] in [LEAGUE]" directive → tiny how-to footnote. The
            // league name is the loudest element on the screen so users
            // can't miss which league we're checking.
            <>
              {previewData && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar
                    src={
                      previewData.sleeperUser.avatar
                        ? `https://sleepercdn.com/avatars/thumbs/${previewData.sleeperUser.avatar}`
                        : undefined
                    }
                    sx={{ width: 36, height: 36 }}
                  >
                    {previewData.sleeperUser.username?.[0]?.toUpperCase()}
                  </Avatar>
                  <Typography variant="body2" color="text.secondary">
                    Verifying{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                      {previewData.sleeperUser.username}
                    </Box>
                  </Typography>
                </Box>
              )}

              {/* Directive — "Change your team name to [CODE]". The code
                  box explicitly opts out of the theme's h-variant
                  uppercase so the displayed string matches what the
                  copy button copies (the code mixes lowercase
                  "dynastyrep.com" with an uppercase suffix). */}
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                Change your team name to
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 2.5,
                  p: 1.5,
                  bgcolor: 'primary.dark',
                  borderRadius: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    color: 'secondary.main',
                    textTransform: 'none',
                    letterSpacing: 0,
                    fontSize: { xs: 14, sm: 16, md: 18 },
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {previewData?.verificationCode}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => handleCopyCode(previewData?.verificationCode || '')}
                  sx={{ color: 'white', ml: 'auto' }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Box>

              {/* League name — the loudest element. Big bordered card so
                  the user can't open the wrong league by mistake. */}
              <Typography
                variant="overline"
                sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}
              >
                In this league
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  mb: 2,
                  borderColor: tokens.orange,
                  borderWidth: 2,
                  bgcolor: 'rgba(232, 114, 42, 0.08)',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Barlow Condensed", sans-serif',
                    fontWeight: 700,
                    fontSize: { xs: 26, sm: 32 },
                    lineHeight: 1.1,
                    color: tokens.orange2,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    wordBreak: 'break-word',
                  }}
                >
                  {previewData?.verifyLeague.name}
                </Typography>
                {previewData?.verifyLeague.season && (
                  <Typography variant="caption" color="text.secondary">
                    {previewData.verifyLeague.season} season
                  </Typography>
                )}
              </Paper>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                In Sleeper: open this league → tap your team → <b>Edit team</b> →
                set <b>Team Name</b> → save.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Not your Sleeper username — just the team name in this one league.
                Revert it anytime after verification.
              </Typography>

              {/* Auto-check indicator. While checking we silently call
                  the verify endpoint every 10s for 3 minutes, so the
                  user can save in Sleeper and see the dialog finish on
                  its own. After the timeout we nudge them to the
                  manual button below. */}
              {autoCheckState === 'checking' && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'background.default',
                  }}
                >
                  <CircularProgress size={14} thickness={5} />
                  <Typography variant="caption" color="text.secondary">
                    Auto-checking every 10s — we'll detect the change once you save it.
                  </Typography>
                </Box>
              )}
              {autoCheckState === 'timeout' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Auto-check paused after 3 minutes. Click <b>Verify &amp; Link</b> below
                  once you've saved the team name in Sleeper.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {linkStep === 'username' ? (
            <>
              <Button onClick={handleCloseLinkDialog}>Cancel</Button>
              <Button
                onClick={handlePreviewAccount}
                variant="contained"
                disabled={!sleeperUsername.trim() || previewMutation.isPending}
              >
                {previewMutation.isPending ? 'Looking up...' : 'Continue'}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={() => {
                  setLinkStep('username');
                  setPreviewData(null);
                  setLinkError(null);
                  setAutoCheckState('idle');
                }}
                startIcon={<ArrowBackIcon />}
              >
                Back
              </Button>
              <Button onClick={handleCloseLinkDialog}>Cancel</Button>
              <Button
                onClick={handleVerifyAndLink}
                variant="contained"
                color="success"
                disabled={verifyAndLinkMutation.isPending}
                startIcon={
                  verifyAndLinkMutation.isPending ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
              >
                {verifyAndLinkMutation.isPending ? 'Verifying...' : 'Verify & Link'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Verification Message Snackbar */}
      <Snackbar
        open={!!verificationMessage}
        autoHideDuration={6000}
        onClose={() => setVerificationMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={verificationMessage?.type}
          onClose={() => setVerificationMessage(null)}
          sx={{ width: '100%' }}
        >
          {verificationMessage?.text}
        </Alert>
      </Snackbar>

      {/* Copy Code Snackbar */}
      <Snackbar
        open={copiedCode}
        autoHideDuration={2000}
        onClose={() => setCopiedCode(false)}
        message="Code copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

/**
 * Three-step "Get Started" widget shown at the top of the dashboard until
 * the user has (1) linked a Sleeper account, (2) verified ownership, and
 * (3) left at least one rating. The whole widget disappears once all three
 * steps are complete.
 *
 * Each row is in one of three states:
 *   - done    → check icon, struck-through label, no action button.
 *   - active  → unchecked icon, primary CTA button. Only ONE step is
 *               active at a time (steps unlock in order).
 *   - locked  → unchecked icon, no CTA. Visible but inert; communicates
 *               what's coming next.
 */
function OnboardingChecklist({
  isVerified,
  hasRated,
  onLinkClick,
}: {
  isVerified: boolean;
  hasRated: boolean;
  onLinkClick: () => void;
}) {
  if (isVerified && hasRated) return null;

  // The link/verify step is one combined action — our verify-and-link
  // flow creates the sleeper_account row only after team-name proof
  // succeeds, so "linked" and "verified" flip true at the same moment.
  // Splitting them into two checklist items would always show one as
  // already-done with no work left to do; collapsing them into a single
  // step matches reality and keeps the focus on a single CTA.
  const stepStates: Array<'done' | 'active' | 'locked'> = (() => {
    if (!isVerified) return ['active', 'locked'];
    if (!hasRated) return ['done', 'active'];
    return ['done', 'done'];
  })();

  const steps = [
    {
      label: 'Link & verify your Sleeper account',
      description:
        "We'll find your dynasty leagues and confirm ownership in one step.",
      activeAction: (
        <Button
          variant="contained"
          color="primary"
          startIcon={<VerifiedUserIcon />}
          onClick={onLinkClick}
          size="small"
        >
          Link & Verify
        </Button>
      ),
    },
    {
      label: 'Rate your first leaguemate',
      description:
        "Help build the trust graph — find someone you've played with.",
      activeAction: (
        <Button
          variant="outlined"
          startIcon={<StarIcon />}
          component={RouterLink}
          to="/top-rated"
          size="small"
        >
          Browse Players
        </Button>
      ),
    },
  ];

  return (
    <Grid item xs={12}>
      <Card
        sx={{
          // Subtle orange wash + accent border so this card visibly demands
          // attention without being garish.
          borderColor: tokens.orange,
          backgroundImage:
            'linear-gradient(180deg, rgba(232,114,42,0.06) 0%, transparent 100%)',
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Get Started
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stepStates.filter((s) => s === 'done').length} of {steps.length}{' '}
              complete
            </Typography>
          </Box>
          <Stack spacing={1.5}>
            {steps.map((step, i) => {
              const state = stepStates[i];
              const done = state === 'done';
              const active = state === 'active';
              return (
                <Box
                  key={step.label}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1,
                    backgroundColor: active
                      ? 'rgba(232,114,42,0.08)'
                      : 'transparent',
                  }}
                >
                  {done ? (
                    <CheckCircleIcon sx={{ color: 'success.main' }} />
                  ) : (
                    <UncheckedIcon
                      sx={{
                        color: active ? tokens.orange : tokens.text3,
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: active ? 700 : 500,
                        color: done ? 'text.secondary' : 'text.primary',
                        textDecoration: done ? 'line-through' : 'none',
                      }}
                    >
                      {step.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {step.description}
                    </Typography>
                  </Box>
                  {active && step.activeAction}
                </Box>
              );
            })}
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
